/**
 * `/admin/dsar` — Task U10 (Tier-3 live-tenant readiness).
 *
 * The company's data-subject request queue, and the one control in this application
 * that destroys a person's data. Everything below is arranged around that fact.
 *
 * ── The screen never decides what an erasure does ───────────────────────────
 *
 * It renders `result` — the object the server wrote when the erasure ran — and it
 * computes nothing from it. The counts, the tables swept, the records retained by
 * law with their ground and citation, and the audit tables kept as evidence are all
 * the server's own words. A screen that summarised them would be a second answer to
 * "what happened to this person's data", and on a regulator's screen the second
 * answer is the one that is wrong.
 *
 * ── The button asks twice, and the second ask is not a dialog ───────────────
 *
 * `window.confirm` is not used, and not because of taste: it is unreadable to a
 * screen reader in some browsers, untestable without jsdom stubbing, and — the real
 * reason — it presents an irreversible action as a two-word question. Instead the
 * row expands into a confirmation panel that states, in the subject's own terms,
 * what is about to happen and what will remain, and the execute button is disabled
 * until the administrator has typed the request id. Typing the id is what makes
 * "I clicked the wrong row" a thing that cannot happen quietly.
 *
 * ── It waits, because the server does ──────────────────────────────────────
 *
 * `POST /dsar/requests/{id}/execute` runs the erasure synchronously and answers when
 * the rows are gone. The button stays disabled for the whole call and the row shows
 * a running state; there is no optimistic update, because there is nothing to be
 * optimistic about — if the call fails halfway, some classes have committed and the
 * honest thing to show is the server's own `erasure_progress`, which the refresh
 * after the call re-reads.
 *
 * ── Refusals render as themselves ───────────────────────────────────────────
 *
 * `409` with `reason: employee_not_offboarded` is the one an administrator will
 * actually hit, and it gets its own sentence rather than the generic conflict text:
 * the fix is a different action in a different place (offboard the person first),
 * and a screen that said only "conflict" would send them to support.
 */

import { Fragment, useCallback, useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { useSession } from '../../auth/SessionProvider';
import { executeDsarErasure, type DsarQueueListResponse, type DsarQueueRow } from '../../api/dsar';
import { useApiQuery } from '../../api/useApiQuery';
import { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { ScreenHeading } from '../../shell/ScreenHeading';

/** `GET /api/v1/dsar/requests`, as `useApiQuery` reads it. */
const QUEUE_PATH = '/api/v1/dsar/requests';

/** Which row is expanded into its confirmation panel, and what has been typed. */
interface Confirming {
  readonly requestId: string;
  readonly typed: string;
}

/**
 * The `details.reason` a `409` carries, when it carries one.
 *
 * Read off the error rather than matched on the message: the message is prose that a
 * later task may reword, and `details.reason` is the field `openapi.yaml` publishes.
 */
function conflictReason(error: WorkforceApiError | null): string | null {
  if (error?.status !== 409) return null;
  const reason = error.details.reason;
  return typeof reason === 'string' ? reason : null;
}

/** Numbers the server counted, rendered in its order. Never recomputed here. */
function countsOf(row: DsarQueueRow): readonly (readonly [string, number])[] {
  const counts = row.result.counts;
  if (counts === null || typeof counts !== 'object' || Array.isArray(counts)) return [];
  return Object.entries(counts as Record<string, unknown>).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number',
  );
}

/** The retained records the server NAMED. A list, not a count — that is the point. */
function retainedOf(row: DsarQueueRow): readonly Record<string, unknown>[] {
  const retained = row.result.retained_by_law;
  return Array.isArray(retained)
    ? retained.filter((item): item is Record<string, unknown> => typeof item === 'object')
    : [];
}

function stringsOf(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export function DsarQueueScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { client, user } = useSession();
  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';

  const [reloadToken, setReloadToken] = useState(0);
  const queue = useApiQuery<DsarQueueListResponse>(QUEUE_PATH, reloadToken);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [error, setError] = useState<WorkforceApiError | null>(null);
  const [finished, setFinished] = useState<DsarQueueRow | null>(null);

  const rows: readonly DsarQueueRow[] = queue.data?.data ?? [];

  /**
   * The rows as the screen shows them: the freshly executed one, when there is one,
   * replaces its stale copy from the read.
   *
   * Not a refetch alone, because the execution's own answer is the newest truth
   * about that row and re-reading could race a colleague's write on another. The
   * refetch still runs — a finished erasure changes nothing else on this list, but
   * "nothing else" is an assumption and the read is cheap.
   */
  const shown = useMemo(
    () => rows.map((row) => (finished !== null && finished.id === row.id ? finished : row)),
    [rows, finished],
  );

  const execute = useCallback(
    async (requestId: string) => {
      setRunning(requestId);
      setError(null);
      try {
        const executed = await executeDsarErasure(client, requestId);
        setFinished(executed);
        setConfirming(null);
        setReloadToken((value) => value + 1);
      } catch (cause) {
        setError(
          cause instanceof WorkforceApiError
            ? cause
            : new WorkforceApiError({
                code: 'CLIENT_TRANSPORT_FAILED',
                message: cause instanceof Error ? cause.message : 'execute failed',
                status: 0,
              }),
        );
      } finally {
        setRunning(null);
      }
    },
    [client],
  );

  const reason = conflictReason(error);

  return (
    <div className="wp-stack" data-testid="u10-dsar-queue">
      <ScreenHeading>{t('nav.dsarQueue')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('dsar.queue.intro')}</p>

      {queue.status === 'loading' ? (
        <Skeleton rows={4} variant="block" label={t('dsar.queue.subject')} testId="u10-skeleton" />
      ) : null}

      {queue.error !== null ? (
        <ApiErrorNote
          error={queue.error}
          subject={t('dsar.queue.subject')}
          onRetry={queue.reload}
        />
      ) : null}

      {error !== null ? (
        reason === 'employee_not_offboarded' ? (
          // Its own sentence, because the fix is a different action in a different
          // place. "Conflict" would send an administrator to support.
          <StatusNote
            severity="medium"
            role="alert"
            title={t('dsar.queue.error.notOffboarded.title')}
            testId="u10-not-offboarded"
          >
            {t('dsar.queue.error.notOffboarded')}
          </StatusNote>
        ) : (
          <ApiErrorNote error={error} subject={t('dsar.queue.subject')} />
        )
      ) : null}

      {queue.status === 'ok' && shown.length === 0 ? (
        <StatusNote severity="info" title={t('dsar.queue.empty')} testId="u10-empty" />
      ) : null}

      {shown.length > 0 ? (
        <div className="wp-table-scroll">
          <table className="wp-table wp-dsar-table">
            <caption className="wp-visually-hidden">{t('dsar.queue.table.caption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('dsar.queue.column.id')}</th>
                <th scope="col">{t('dsar.queue.column.employee')}</th>
                <th scope="col">{t('dsar.queue.column.kind')}</th>
                <th scope="col">{t('dsar.queue.column.status')}</th>
                <th scope="col">{t('dsar.queue.column.requested')}</th>
                <th scope="col">{t('dsar.queue.column.result')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const counts = countsOf(row);
                const retained = retainedOf(row);
                const evidence = stringsOf(row.result.retained_as_evidence);
                const isConfirming = confirming?.requestId === row.id;
                const canExecute = row.kind === 'erasure' && row.status !== 'done';
                return (
                  <Fragment key={row.id}>
                    <tr
                      data-testid={`u10-row-${row.id}`}
                      {...(isConfirming ? { 'data-open': '' } : {})}
                    >
                      <th scope="row" className="wp-mono">
                        {row.id}
                      </th>
                      <td className="wp-mono">{row.employee_id}</td>
                      <td>
                        <Badge
                          severity={row.kind === 'erasure' ? 'high' : 'info'}
                          icon={row.kind === 'erasure' ? 'circle-alert' : 'circle-check'}
                          label={t(
                            row.kind === 'erasure' ? 'dsar.kind.erasure' : 'dsar.kind.export',
                          )}
                        />
                      </td>
                      <td>
                        {row.status}
                        {row.erasure_progress.length > 0 && row.status !== 'done' ? (
                          // A half-executed request is visible as partial rather than as
                          // stuck: these are the classes that really committed, and a
                          // re-execution resumes after them.
                          <span data-testid={`u10-progress-${row.id}`}>
                            {' '}
                            {t('dsar.queue.progress')} {row.erasure_progress.join(', ')}
                          </span>
                        ) : null}
                      </td>
                      <td>{formatInstant(row.requested_at, locale, timezone)}</td>
                      <td>
                        {counts.length > 0 ? (
                          <ul className="wp-dsar-counts" data-testid={`u10-counts-${row.id}`}>
                            {counts.map(([key, value]) => (
                              <li key={key}>
                                <span className="wp-small wp-muted">{key}:</span>{' '}
                                <span className="wp-mono">{value}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}

                        {retained.length > 0 ? (
                          <div data-testid={`u10-retained-${row.id}`}>
                            <p className="wp-label">{t('dsar.queue.retained')}</p>
                            <ul className="wp-dsar-counts">
                              {retained.map((record, index) => (
                                <li
                                  className="wp-small"
                                  key={`${String(record.table)}-${String(record.id)}-${index}`}
                                >
                                  {String(record.table)} id={String(record.id)} (
                                  {String(record.kind)}) — {String(record.ground)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        {evidence.length > 0 ? (
                          <p className="wp-small" data-testid={`u10-evidence-${row.id}`}>
                            {t('dsar.queue.evidence')} {evidence.join(', ')}
                          </p>
                        ) : null}

                        {canExecute && !isConfirming ? (
                          // Step one of two, and it is not the dangerous one: it opens the
                          // drawer under the row. Danger as TEXT until the confirm.
                          <button
                            type="button"
                            className="wp-button wp-button--sm wp-button--danger"
                            onClick={() => {
                              setConfirming({ requestId: row.id, typed: '' });
                              setError(null);
                            }}
                            data-testid={`u10-start-${row.id}`}
                          >
                            {t('dsar.queue.execute')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {/* Step two, in a drawer **under its own row** rather than in a modal:
                    the administrator has to be able to read the id in the row above
                    while typing it, and a dialog covers exactly that. */}
                    {isConfirming ? (
                      <tr className="wp-drawer-row">
                        <td colSpan={6}>
                          <div className="wp-drawer" data-testid={`u10-confirm-${row.id}`}>
                            {/* States what will happen and what will remain, then asks
                            for the id. A dialog with two words would present an
                            irreversible deletion as a formality. */}
                            <p>{t('dsar.queue.confirm.warning')}</p>
                            <p className="wp-small wp-muted">{t('dsar.queue.confirm.remains')}</p>
                            <div className="wp-field">
                              <label className="wp-field__label" htmlFor={`u10-typed-${row.id}`}>
                                {t('dsar.queue.confirm.typeId')}
                              </label>
                              <input
                                className="wp-field__input wp-mono"
                                id={`u10-typed-${row.id}`}
                                data-testid={`u10-typed-${row.id}`}
                                type="text"
                                value={confirming.typed}
                                onChange={(event) => {
                                  setConfirming({ requestId: row.id, typed: event.target.value });
                                }}
                              />
                            </div>
                            <div className="wp-drawer__actions">
                              <button
                                type="button"
                                className="wp-button wp-button--danger"
                                disabled={confirming.typed.trim() !== row.id || running !== null}
                                onClick={() => {
                                  void execute(row.id);
                                }}
                                data-testid={`u10-execute-${row.id}`}
                              >
                                {running === row.id
                                  ? t('dsar.queue.executing')
                                  : t('dsar.queue.confirm.execute')}
                              </button>
                              <button
                                type="button"
                                className="wp-button"
                                onClick={() => {
                                  setConfirming(null);
                                }}
                                data-testid={`u10-cancel-${row.id}`}
                              >
                                {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
